import {Controller} from "@hotwired/stimulus"
import {startIcons} from "../import_lucid.js"

export default class extends Controller {
    static values = {
        organizationSlug: String,
        entitySearch: { type: Boolean, default: false }
    }

    static targets = ["modalBg", "modal", "search", "results"]
    items = []

    connect() {
        if (!this.boundKeydown) {
            this.boundKeydown = this.handleKeydown.bind(this)
            window.addEventListener("keydown", this.boundKeydown)
        }
        this.activeIndex = -1
        this.suppressMouseHover = false
        this.boundMouseMove = () => {
            if (this.suppressMouseHover) {
                this.suppressMouseHover = false
            }
        }
        window.addEventListener("mousemove", this.boundMouseMove)
    }

    disconnect() {
        if (this.boundKeydown) {
            window.removeEventListener("keydown", this.boundKeydown)
            this.boundKeydown = null
        }
        window.removeEventListener("mousemove", this.boundMouseMove)
    }

    closeCommandPalette() {
        this.modalBgTarget.classList.add("hidden")
        this.modalTarget.classList.add("hidden")
        this.searchTarget.value = ""
        this.resultsTarget.innerHTML = ""
        let quickLinks = this.element.querySelector(`[data-id='quick-links']`)
        quickLinks?.classList.remove("hidden")
    }

    openCommandPalette() {
        this.modalTarget.classList.remove("hidden")
        this.modalBgTarget.classList.remove("hidden")
        this.searchTarget.focus()
    }

    openSearchPalette(event) {
        event.stopPropagation()
        this.modalTarget.classList.remove("hidden")
        this.modalBgTarget.classList.remove("hidden")
        this.searchTarget.focus()
        this.fetchItems()
    }

    handleKeydown(event) {
        if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
            event.preventDefault()
            this.openCommandPalette()
            this.fetchItems()
        } else if (event.key === "Escape") {
            event.preventDefault()
            this.closeCommandPalette()
        }

        if (!this.modalTarget.classList.contains("hidden")) {
            switch (event.key) {
                case "Enter":
                    event.preventDefault()
                    this.selectActiveItem(event.metaKey || event.ctrlKey)
                    this.closeCommandPalette()
                    break
                case "ArrowDown":
                    event.preventDefault()
                    this.moveSelection(1)
                    break
                case "ArrowUp":
                    event.preventDefault()
                    this.moveSelection(-1)
                    break
            }
        }
    }

    moveSelection(direction) {
        const items = this.resultsTarget.querySelectorAll("[data-item]")
        if (items.length === 0) return
        this.suppressMouseHover = true
        this.clearActiveClasses()

        this.activeIndex += direction
        if (this.activeIndex < 0) this.activeIndex = items.length - 1
        if (this.activeIndex >= items.length) this.activeIndex = 0

        const activeItem = items[this.activeIndex]
        activeItem.classList.add("bg-indigo-50")
        activeItem.scrollIntoView({ behavior: "smooth", block: "nearest" })
    }

    selectActiveItem(openInNewTab = false) {
        const items = this.resultsTarget.querySelectorAll("[data-item]")
        const activeItem = items[this.activeIndex]
        if (activeItem) {
            const link = activeItem.querySelector("a")
            if (link) {
                if (openInNewTab) {
                    window.open(link.href, '_blank')
                } else {
                    link.click()
                }
            }
        }
    }

    async fetchItems() {
        try {
            const slug = this.organizationSlugValue
            const response = await fetch(`/${slug}/command_palette`)
            this.items = await response.json()
            this.filterItems()
        } catch (error) {
            console.error("Error fetching command palette items:", error)
        }
    }

    groupBySection(items) {
        return items.reduce((acc, item) => {
            (acc[item.section] ||= []).push(item)
            return acc
        }, {})
    }

    filterItems() {
        let query = this.searchTarget.value.toLowerCase().trim()
        let quickLinks = this.element.querySelector(`[data-id='quick-links']`)
        if (!query) {
            this.resultsTarget.innerHTML = ""
            quickLinks?.classList.remove("hidden")
            return
        }
        quickLinks?.classList.add("hidden")
        let filtered = query
            ? this.items.filter(item =>
                item.name.toLowerCase().includes(query.toLowerCase())
            )
            : this.items

        this.resultsTarget.innerHTML = ""
        this.activeIndex = -1

        const grouped = this.groupBySection(filtered)
        this.renderGroupedItems(grouped)

        // Live entity search for queries 3+ chars (permission-gated)
        if (query.length >= 3 && this.entitySearchValue) {
            this.searchEntities(query)
        }
    }

    async searchEntities(query) {
        if (this._entitySearchTimer) clearTimeout(this._entitySearchTimer)
        this._entitySearchTimer = setTimeout(async () => {
            // Show loader
            const existing = this.resultsTarget.querySelector("[data-entity-search]")
            if (existing) existing.remove()

            const loader = document.createElement("div")
            loader.dataset.entitySearch = "true"
            loader.innerHTML = `<div class="px-4 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider">Records</div>
                <div class="flex items-center gap-2 px-4 py-3 text-sm text-gray-400">
                    <span class="lz-spinner-icon h-4 w-4"></span> Searching records...
                </div>`
            this.resultsTarget.appendChild(loader)

            try {
                const slug = this.organizationSlugValue
                const response = await fetch(`/${slug}/entities/search?q=${encodeURIComponent(query)}`)
                if (!response.ok) throw new Error(`HTTP ${response.status}`)
                const results = await response.json()

                // Remove loader
                const loaderEl = this.resultsTarget.querySelector("[data-entity-search]")
                if (loaderEl) loaderEl.remove()

                if (results.length === 0) return

                const container = document.createElement("div")
                container.dataset.entitySearch = "true"

                const header = document.createElement("div")
                header.className = "px-4 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider"
                header.textContent = "Records"
                container.appendChild(header)

                results.forEach(result => {
                    if (result.report_links && result.report_links.length > 0) {
                        // Show report links for this entity
                        result.report_links.forEach(link => {
                            const reportRow = this.createResultRow(
                                link.url,
                                `${result.display} — ${link.label}`,
                                result.table_name,
                                "bar-chart",
                                "bg-indigo-50",
                                "text-indigo-600"
                            )
                            container.appendChild(reportRow)
                        })
                    } else {
                        // No report links configured — show entity as non-clickable info
                        const infoRow = document.createElement("div")
                        infoRow.className = "flex items-center gap-3 px-4 py-2.5 text-sm text-gray-400"
                        infoRow.innerHTML = `<span class="flex-shrink-0 w-7 h-7 rounded-md bg-gray-50 flex items-center justify-center"><i data-lucide="database" class="w-4 h-4 text-gray-400"></i></span>
                            <div><div class="font-medium text-gray-500">${this.escapeHtml(result.display)}</div><div class="text-[11px] text-gray-400">${this.escapeHtml(result.table_name)} — no report linked</div></div>`
                        if (window.lucide) window.lucide.createIcons()
                        container.appendChild(infoRow)
                    }
                })

                this.resultsTarget.appendChild(container)
            } catch (error) {
                // Remove loader on error
                const loaderEl = this.resultsTarget.querySelector("[data-entity-search]")
                if (loaderEl) loaderEl.remove()
            }
        }, 300)
    }

    createResultRow(href, name, subtitle, icon, iconBg, iconColor) {
        const row = document.createElement("div")
        row.dataset.item = "true"
        row.className = "flex items-center gap-3 px-4 py-2 mx-2 rounded-lg cursor-pointer transition-colors"

        row.innerHTML = `<div class="w-7 h-7 flex items-center justify-center ${iconBg} rounded-md flex-shrink-0">
                <span class="${iconColor} text-xs"><i class="${icon}"></i></span>
            </div>
            <div class="flex-1 min-w-0">
                <a href="${this.escapeHtml(href)}" class="block text-sm text-gray-700 truncate">${this.escapeHtml(name)}</a>
                ${subtitle ? `<span class="text-[11px] text-gray-400">${this.escapeHtml(subtitle)}</span>` : ''}
            </div>`

        row.addEventListener("mouseenter", () => {
            if (this.suppressMouseHover) return
            this.clearActiveClasses()
            row.classList.add("bg-indigo-50")
            this.activeIndex = Array.from(this.resultsTarget.querySelectorAll("[data-item]")).indexOf(row)
        })

        row.addEventListener("mouseleave", () => {
            row.classList.remove("bg-indigo-50")
        })

        return row
    }

    renderGroupedItems(grouped) {
        Object.entries(grouped).forEach(([section, items]) => {
            const header = document.createElement("div")
            header.className = "px-4 py-2 text-[11px] font-medium text-gray-400 uppercase tracking-wider"
            header.textContent = section
            this.resultsTarget.appendChild(header)

            items.forEach((item) => {
                const iconBg = section === 'Entities' ? 'bg-indigo-50' : 'bg-indigo-50'
                const iconColor = section === 'Entities' ? 'text-indigo-500' : 'text-indigo-500'

                const row = this.createResultRow(
                    item.path,
                    item.name,
                    null,
                    item.icon,
                    iconBg,
                    iconColor
                )

                // Override with lucide icons and colour tints for report items
                const colour = item.colour || "#6366f1"
                const tint = `${colour}1a`
                const iconHtml = `<div class="w-8 h-8 flex items-center justify-center rounded-md mr-3" style="background-color: ${tint};">
                    <i data-lucide="${item.icon || 'file-text'}" class="w-4 h-4" style="color: ${colour};"></i>
                  </div>`

                row.innerHTML = `${iconHtml}
                      <div class="flex-1">
                          <a href="${item.path}" class="block text-sm font-medium text-gray-900 truncate">${item.name}</a></div>`

                this.resultsTarget.appendChild(row)
            })
        })

        startIcons(this.resultsTarget)

        // Auto-select first item
        const firstItem = this.resultsTarget.querySelector("[data-item]")
        if (firstItem) {
            this.activeIndex = 0
            firstItem.classList.add("bg-indigo-50")
        }
    }

    clearActiveClasses() {
        this.resultsTarget
            .querySelectorAll("[data-item]")
            .forEach((el) => el.classList.remove("bg-indigo-50"))
    }

    escapeHtml(str) {
        const div = document.createElement("div")
        div.textContent = str
        return div.innerHTML
    }
}
